import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../../../api/useApiRequest';
import { copyHtmlElementToClipboard, copyTextToClipboard } from '../../../helpers/copyToClipboard';
import { saveFile } from '../../../helpers/fileDownload';
import { useTemporaryBoolean } from '../../../helpers/hooks/useTemporaryBoolean';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { Alert } from '../../../shared/Alert';
import { SecondaryButton, SecondaryProgressButton } from '../../../shared/Button';
import { ErrorBox } from '../../../shared/ErrorBox';
import { Expandable } from '../../../shared/Expandable';
import { ExpandControlChevron } from '../../../shared/ExpandControlChevron';
import { FadingMessage } from '../../../shared/FadingMessage';
import { fontSize03, fontSize04 } from '../../../styling/fonts';
import { spacing03, spacing04 } from '../../../styling/spacing';
import {
  HeatSourceResponse,
  PipeResponse,
  PumpResponse,
} from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { getTreeWithStats } from '../../shared/quote-or-design-form/map/map-helpers/getTreeWithStats';
import { getMapDisplayStatus } from '../../shared/quote-or-design-form/map/map-helpers/mapHelpers';
import { getStatsData, Tree } from '../../shared/quote-or-design-form/map/map-helpers/treeHelpers';
import { validateTree } from '../../shared/quote-or-design-form/map/map-helpers/treeValidator';
import { DataForDesignDrawingsUnit, DataForDesignDrawingsManifold } from './dataForDesignDrawingsResponse';
import { ManifoldDataForDesignDrawings } from './ManifoldDataForDesignDrawings';
import { ManifoldTable } from './ManifoldTable';
import { heatSourceSizingText, getShortfallTextOrNull } from './text-for-design-drawings';
import { projectUrls } from '../../projects/projectUrls';
import { SecondaryButtonLink } from '../../../shared/Link';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';

export const expandUnitChevronTestId = (unit: DataForDesignDrawingsUnit) => `expand-unit-${unit.unitId}`;

type Props = {
  projectId: number;
  designId: number;
  unit: DataForDesignDrawingsUnit;
  externalAirTemperatureInDegreesCelsius: number;
  unitPumps: Array<PumpResponse>;
  manifoldPumps: Array<PumpResponse>;
  pipes: Array<PipeResponse>;
  heatSources: Array<HeatSourceResponse>;
};

const getMapDisplayStatusFromJson = (
  pipelineMapJson: string,
  manifolds: Array<DataForDesignDrawingsManifold>,
  unitPumps: Array<PumpResponse>,
  manifoldPumps: Array<PumpResponse>,
  heatSources: Array<HeatSourceResponse>,
  pipes: Array<PipeResponse>,
): string => {
  if (!pipelineMapJson) {
    return '';
  }

  const tree = JSON.parse(pipelineMapJson) as Tree;
  const statsData = getStatsData(manifolds, unitPumps, manifoldPumps, heatSources, pipes);
  const treeWithStats = getTreeWithStats(tree, statsData);
  return getMapDisplayStatus(validateTree(treeWithStats, manifolds.length)).status;
};

export const UnitDataForDesignDrawings = ({
  projectId,
  designId,
  unit,
  externalAirTemperatureInDegreesCelsius,
  unitPumps,
  manifoldPumps,
  pipes,
  heatSources,
}: Props) => {
  const downloadHeatLossExport = useApiRequest<ArrayBuffer>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/designs/${designId}/export-heat-loss/${unit.unitId}`,
  });

  const downloadCuttingPlanExport = useApiRequest<ArrayBuffer>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/designs/${designId}/export-cutting-plan/${unit.unitId}`,
  });

  const [isExpanded, setExpanded] = useState(true);
  const [shouldShowCopiedMessage, showCopiedMessage] = useTemporaryBoolean();
  const [copiedItemName, setCopiedItemName] = useState('');
  const manifoldTablesRef = useRef<HTMLTableElement>(null);

  const copyManifoldTableToClipboard = () => {
    if (manifoldTablesRef.current) {
      // Copying doesn't work when an element has display:none
      manifoldTablesRef.current.style.display = 'block';
      copyHtmlElementToClipboard(manifoldTablesRef.current);
      manifoldTablesRef.current.style.display = 'none';
      setCopiedItemName('manifold tables');
      showCopiedMessage();
    }
  };

  const shortfallText = getShortfallTextOrNull(unit, externalAirTemperatureInDegreesCelsius);

  const copyShortfalls = () => {
    if (shortfallText != null) {
      copyTextToClipboard(shortfallText);
      setCopiedItemName('shortfalls');
      showCopiedMessage();
    }
  };

  const copyHeatSourceSizing = () => {
    copyTextToClipboard(heatSourceSizingText(unit.heatSource, assertNotNull(unit.totalHeatLossInWatts)));
    setCopiedItemName('heat source sizing');
    showCopiedMessage();
  };

  const mapDisplayStatus = getMapDisplayStatusFromJson(
    unit.pipelineMapJson,
    unit.manifolds,
    unitPumps,
    manifoldPumps,
    heatSources,
    pipes,
  );
  const unitName = `${unit.unitName}${mapDisplayStatus ? ` (${mapDisplayStatus})` : ''}`;

  return (
    <UnitContainer>
      {downloadHeatLossExport.error != null && <ApiErrorBox error={downloadHeatLossExport.error} />}
      {downloadCuttingPlanExport.error != null && <ApiErrorBox error={downloadCuttingPlanExport.error} />}
      <HeaderRow>
        <ExpandControlChevron
          isExpanded={isExpanded}
          onClick={() => setExpanded(!isExpanded)}
          testId={expandUnitChevronTestId(unit)}
        />
        <UnitName>{unitName}</UnitName>
        {unit.totalHeatLossInWatts != null ? (
          <>
            <SecondaryProgressButton
              inProgress={downloadHeatLossExport.inProgress}
              onClick={() =>
                downloadHeatLossExport.sendRequest({
                  onSuccess: saveFile,
                })
              }
            >
              Export Heat Loss
            </SecondaryProgressButton>
            <SecondaryProgressButton
              inProgress={downloadCuttingPlanExport.inProgress}
              onClick={() =>
                downloadCuttingPlanExport.sendRequest({
                  onSuccess: saveFile,
                })
              }
            >
              Export Cutting Plan Data
            </SecondaryProgressButton>
            <SecondaryButton onClick={copyManifoldTableToClipboard}>Copy All Manifold Tables</SecondaryButton>
            {mapDisplayStatus === 'Map Complete' && (
              <>
                <IfUserHasPermission requiredPermission="Edit">
                  <SecondaryButtonLink to={projectUrls.editPdfDetails(projectId, designId, unit.unitId)}>
                    Configure PDF
                  </SecondaryButtonLink>
                </IfUserHasPermission>
                {unit.hasConfiguredPdf && (
                  <SecondaryButtonLink
                    to={projectUrls.manifoldDiagramPdf(projectId, designId, unit.unitId)}
                    target="_blank"
                  >
                    View PDF
                  </SecondaryButtonLink>
                )}
              </>
            )}
            <SecondaryButton onClick={copyShortfalls} disabled={shortfallText == null}>
              Copy Shortfalls
            </SecondaryButton>
            <SecondaryButton onClick={copyHeatSourceSizing}>Copy Heat Source Sizing</SecondaryButton>
            <FadingMessage $visible={shouldShowCopiedMessage}>Copied {copiedItemName}</FadingMessage>
          </>
        ) : (
          <Alert type="warning" small={true} body="This unit has no heat loss data" />
        )}
      </HeaderRow>
      <HiddenElement ref={manifoldTablesRef}>
        {unit.manifolds.map(manifold => (
          <ManifoldTable manifold={manifold} key={manifold.manifoldId} />
        ))}
      </HiddenElement>
      <Expandable isExpanded={isExpanded}>
        <UnitDetails>
          {unit.manifolds.map(manifold => (
            <ManifoldDataContainer key={manifold.manifoldId}>
              <ManifoldDataForDesignDrawings manifold={manifold} />
            </ManifoldDataContainer>
          ))}
          {unit.manifolds.length === 0 && <NoManifoldsMessage>This unit has no manifolds</NoManifoldsMessage>}
        </UnitDetails>
      </Expandable>
    </UnitContainer>
  );
};

const UnitContainer = styled.div`
  margin-bottom: ${spacing04};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;

  > :not(:last-child) {
    margin-right: ${spacing03};
  }
`;

const UnitDetails = styled.div`
  margin-left: ${spacing04};
`;

const UnitName = styled.span`
  margin-right: ${spacing03};
  font-size: ${fontSize04};
`;

const ApiErrorBox = styled(ErrorBox)`
  margin-bottom: ${spacing03};
`;

const ManifoldDataContainer = styled.div`
  margin-top: ${spacing04};
`;

const HiddenElement = styled.div`
  display: none;
  opacity: 0;
  pointer-events: none;
  position: absolute;
`;

const NoManifoldsMessage = styled.div`
  margin-top: ${spacing04};
  font-size: ${fontSize03};
  font-style: italic;
`;
